/**
 * The one place the active company id lives, per browser tab.
 *
 * - sessionStorage holds this tab's company, so two tabs can work on two
 *   companies side by side and a switch in one never moves the other.
 * - localStorage holds the last company used, only to seed a new tab.
 * - The API wrapper reads the X-Company-Id header from here at request time,
 *   so the header can never lag behind what the UI shows.
 *
 * No imports: api.ts and active-company.ts both depend on this module.
 */
const KEY = 'hexdi.activeCompanyId';

type Listener = () => void;
const listeners = new Set<Listener>();

let current: number | undefined = readInitial();

function parse(raw: string | null): number | undefined {
  if (!raw) return undefined;
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : undefined;
}

function readInitial(): number | undefined {
  if (typeof window === 'undefined') return undefined;
  const own = parse(window.sessionStorage.getItem(KEY));
  if (own !== undefined) return own;
  // A new tab: take the last company used and make it this tab's own, so a
  // reload keeps it even after another tab switches.
  const seed = parse(window.localStorage.getItem(KEY));
  if (seed !== undefined) window.sessionStorage.setItem(KEY, String(seed));
  return seed;
}

function write(id: number) {
  current = id;
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(KEY, String(id));
  window.localStorage.setItem(KEY, String(id));
}

export const activeCompanyStore = {
  get(): number | undefined {
    return current;
  },
  /** Switch company: stores it and re-renders every subscriber. */
  set(id: number): void {
    write(id);
    listeners.forEach((l) => l());
  },
  /**
   * Record a fallback chosen while rendering (the stored company is gone).
   * It does not notify: the component resolving it already renders this id.
   */
  adopt(id: number): void {
    write(id);
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
